import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  Clock, 
  User, 
  CheckCircle, 
  X, 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Briefcase
} from 'lucide-react';
import { consultantsService, ConsultantService } from '../../services/consultants.service';
import { trainingsService } from '../../services/trainings.service';
import { useAuth } from '../../context/AuthContext';

interface Creneau {
  id: number;
  service_id: string;
  start_time: string;
  end_time: string;
  date: string;
  is_available: boolean;
  is_booked: boolean;
  price?: number;
}

const CreneauxReservationPage: React.FC = () => {
  const { id, serviceId } = useParams<{ id: string; serviceId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [service, setService] = useState<ConsultantService | null>(null);
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCreneau, setSelectedCreneau] = useState<Creneau | null>(null);
  const [reservationData, setReservationData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (serviceId) {
      loadServiceData();
    }
  }, [serviceId]);

  const loadServiceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const serviceData = await consultantsService.getService(serviceId!);
      setService(serviceData);

      // Track view - services are stored in trainings table with service_type = 'consulting_service'
      // So we can use the same tracking method as trainings
      try {
        await trainingsService.trackTrainingView(serviceId!);
      } catch (viewError) {
        // Don't fail the entire load if view tracking fails
      }

      // Load available creneaux for this service
      // This would be a real API call in production
      const mockCreneaux: Creneau[] = [
        {
          id: 1,
          service_id: serviceId!,
          start_time: '09:00',
          end_time: '10:00',
          date: '2024-01-15',
          is_available: true,
          is_booked: false,
          price: 5000
        },
        {
          id: 2,
          service_id: serviceId!,
          start_time: '10:00',
          end_time: '11:00',
          date: '2024-01-15',
          is_available: true,
          is_booked: false,
          price: 5000
        },
        {
          id: 3,
          service_id: serviceId!,
          start_time: '14:00',
          end_time: '15:00',
          date: '2024-01-16',
          is_available: true,
          is_booked: false,
          price: 5000
        },
        {
          id: 4,
          service_id: serviceId!,
          start_time: '15:00',
          end_time: '16:00',
          date: '2024-01-16',
          is_available: false,
          is_booked: true,
          price: 5000
        }
      ];

      setCreneaux(mockCreneaux);
    } catch (error) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreneauSelect = (creneau: Creneau) => {
    if (!creneau.is_available || creneau.is_booked) return;
    setSelectedCreneau(creneau);
  };

  const handleReservationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCreneau || !isAuthenticated) {
      setError('Veuillez sélectionner un créneau et vous connecter');
      return;
    }

    try {
      setSubmitting(true);
      
      // This would be a real API call in production
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show success message and redirect
      alert('Réservation effectuée avec succès! Vous recevrez un email de confirmation.');
      navigate(`/consultant/${id}`);
    } catch (error) {
      setError(t('public.creneauxReservation.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-brand-primary/5 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-brand-primary mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-brand-primary/5 flex items-center justify-center">
        <div className="text-center">
          <div className="p-4 bg-red-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Service non trouvé
          </h3>
          <p className="text-gray-600 mb-6">
            {error || 'Le service demandé n\'existe pas ou a été supprimé.'}
          </p>
          <button
            onClick={() => navigate(`/consultant/${id}`)}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-lg"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-brand-primary/5">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/consultant/${id}`)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-primary transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>

          {/* Service Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Réservation - {service.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sélectionnez un créneau disponible pour votre consultation
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Available Creneaux */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Créneaux disponibles</h2>
            
            {creneaux.length > 0 ? (
              <div className="space-y-4">
                {creneaux.map((creneau) => (
                  <motion.div
                    key={creneau.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-6 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                      creneau.is_available && !creneau.is_booked
                        ? selectedCreneau?.id === creneau.id
                          ? 'border-brand-primary bg-brand-primary/5'
                          : 'border-gray-200 hover:border-brand-primary/50 hover:bg-brand-primary/5'
                        : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    }`}
                    onClick={() => handleCreneauSelect(creneau)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-brand-primary/10 to-brand-primary/20 rounded-lg">
                          <Calendar className="h-6 w-6 text-brand-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {formatDate(creneau.date)}
                          </h3>
                          <div className="flex items-center gap-4 text-gray-600">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>{formatTime(creneau.start_time)} - {formatTime(creneau.end_time)}</span>
                            </div>
                            {creneau.price && (
                              <div className="text-brand-primary font-semibold">
                                {creneau.price.toLocaleString()} DZD
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {creneau.is_booked ? (
                          <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                            Réservé
                          </span>
                        ) : creneau.is_available ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                            Disponible
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full">
                            Indisponible
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="p-4 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Calendar className="h-8 w-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('common.noResults')}
                </h3>
                <p className="text-gray-600">
                  {t('public.creneauxReservation.subtitle')}
                </p>
              </div>
            )}
          </div>

          {/* Reservation Form */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('public.creneauxReservation.title')}</h2>
            
            {!isAuthenticated ? (
              <div className="text-center py-8">
                <div className="p-4 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <User className="h-8 w-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Connexion requise
                </h3>
                <p className="text-gray-600 mb-6">
                  Vous devez être connecté pour réserver un créneau.
                </p>
                <button
                  onClick={() => navigate('/auth/role-selection')}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-lg"
                >
                  Se connecter
                </button>
              </div>
            ) : selectedCreneau ? (
              <form onSubmit={handleReservationSubmit} className="space-y-6">
                {/* Selected Creneau Info */}
                <div className="p-4 bg-brand-primary/5 border border-brand-primary/20 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Créneau sélectionné</h3>
                  <div className="flex items-center gap-4 text-gray-700">
                    <Calendar className="h-4 w-4 text-brand-primary" />
                    <span>{formatDate(selectedCreneau.date)}</span>
                    <Clock className="h-4 w-4 text-brand-primary" />
                    <span>{formatTime(selectedCreneau.start_time)} - {formatTime(selectedCreneau.end_time)}</span>
                  </div>
                </div>

                {/* User Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet
                    </label>
                    <input
                      type="text"
                      value={reservationData.name}
                      onChange={(e) => setReservationData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={reservationData.email}
                      onChange={(e) => setReservationData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={reservationData.phone}
                      onChange={(e) => setReservationData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message (optionnel)
                    </label>
                    <textarea
                      value={reservationData.message}
                      onChange={(e) => setReservationData(prev => ({ ...prev, message: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                      placeholder="Décrivez vos besoins ou questions..."
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-4 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-brand-primary/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Réservation en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Confirmer la réservation
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="p-4 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Calendar className="h-8 w-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Sélectionnez un créneau
                </h3>
                <p className="text-gray-600">
                  Choisissez un créneau disponible dans la liste à gauche pour continuer.
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-red-800">{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreneauxReservationPage;
